import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";

const authHeaders = () => {
  const t = localStorage.getItem(AUTH_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * AdminTeslaFleet — owner-only diagnostic view.
 *
 * URL: /admin/tesla-fleet
 *
 * For every Tesla we've cached vehicle_data for, this shows a single row
 * with the trim-specific EPA baseline, Tesla's own claimed range, and
 * both SoH values (vs EPA + vs Tesla-claim) side by side. Useful for
 * validating that the app is computing SoH correctly across real
 * connected vehicles and for flagging any packs that are close to the
 * warranty floor.
 */
export default function AdminTeslaFleet() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/admin/me`, { credentials: "include", headers: authHeaders() })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/tesla-fleet`, {
        credentials: "include",
        headers: authHeaders(),
      });
      if (r.status === 401) {
        setAuthed(false);
        return;
      }
      if (!r.ok) throw new Error(`Fleet load failed (${r.status})`);
      setData(await r.json());
    } catch (e) {
      setError(e.message || "Failed to load fleet diagnostic");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="fleet-checking-auth">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (authed === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="fleet-unauth">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-[#FFB020]" strokeWidth={2} />
          <div className="text-sm mb-4">
            You need to sign in as admin to view this page.
          </div>
          <button
            onClick={() => navigate("/admin")}
            className="rounded-lg border border-white/20 px-4 py-2 text-sm hover:bg-white/[0.04]"
            data-testid="fleet-goto-login"
          >
            Go to admin login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-tesla-fleet-page">
      <div className="max-w-6xl mx-auto px-5 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-white transition-colors mb-2"
              data-testid="fleet-back-link"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={2.4} />
              Admin dashboard
            </Link>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
              Tesla fleet diagnostic
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Real SoH vs EPA vs Tesla-claimed across every connected vehicle.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs hover:bg-white/[0.04] disabled:opacity-50"
            data-testid="fleet-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2.2} />
            Refresh
          </button>
        </div>

        {/* Summary KPIs */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6" data-testid="fleet-summary">
            <SummaryKpi
              label="Connected users"
              value={data.connected_users}
              testid="fleet-kpi-connected"
            />
            <SummaryKpi
              label="Vehicles with data"
              value={data.vehicles_with_data}
              testid="fleet-kpi-vehicles"
            />
            <SummaryKpi
              label="At warranty risk"
              value={
                data.rows.filter(
                  (r) => r.soh_headroom_pct !== null && r.soh_headroom_pct <= 5
                ).length
              }
              tone={
                data.rows.some(
                  (r) => r.soh_headroom_pct !== null && r.soh_headroom_pct <= 5
                )
                  ? "warn"
                  : "ok"
              }
              testid="fleet-kpi-risk"
            />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-[#FF6B57]/40 bg-[#FF6B57]/[0.08] px-4 py-3 text-sm text-[#FF6B57] mb-4" data-testid="fleet-error">
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-16" data-testid="fleet-loading">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        )}

        {data && data.rows.length === 0 && !loading && (
          <div
            className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-sm text-zinc-400"
            data-testid="fleet-empty"
          >
            No connected vehicles have reported <code className="font-mono text-white">vehicle_data</code> yet.
            Fleet rows appear here once a user connects their Tesla and the app has fetched
            at least one battery snapshot.
          </div>
        )}

        {data && data.rows.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="fleet-table-wrap">
            <table className="w-full text-[12px]" data-testid="fleet-table">
              <thead>
                <tr className="text-left text-zinc-500 font-mono text-[10px] uppercase tracking-wider border-b border-white/5">
                  <th className="px-3 py-2.5">Vehicle</th>
                  <th className="px-3 py-2.5">VIN</th>
                  <th className="px-3 py-2.5 text-right">Live range</th>
                  <th className="px-3 py-2.5 text-right">Range @ 100%</th>
                  <th className="px-3 py-2.5 text-right">EPA</th>
                  <th className="px-3 py-2.5 text-right">Tesla claim</th>
                  <th className="px-3 py-2.5 text-right">SoH vs EPA</th>
                  <th className="px-3 py-2.5 text-right">SoH vs Tesla</th>
                  <th className="px-3 py-2.5 text-right">Warranty headroom</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, idx) => (
                  <FleetRow key={r.vin_last4 + idx} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Methodology footnote */}
        <div className="mt-6 rounded-xl border border-white/5 bg-white/[0.01] px-4 py-3 text-[11px] text-zinc-500 leading-relaxed" data-testid="fleet-methodology">
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Methodology</div>
          Live range comes from Tesla's <code className="font-mono text-zinc-400">battery_range</code> field at the vehicle's current SoC.
          Range @ 100% is that value scaled to full charge. SoH % is Range @ 100% divided by
          the EPA-certified baseline for the exact trim and by Tesla's spec-sheet claim,
          respectively. Warranty headroom is SoH vs EPA minus the warranty floor (typically 70%).
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
const SummaryKpi = ({ label, value, tone, testid }) => {
  const color = tone === "warn" ? "#FFB020" : tone === "ok" ? "#39FF14" : "#FFFFFF";
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
      data-testid={testid}
    >
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        {label}
      </div>
      <div className="font-display text-2xl font-semibold leading-none" style={{ color }}>
        {value}
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
const FleetRow = ({ row }) => {
  const {
    display_name, model, year, trim,
    vin_masked,
    battery_range_mi, usable_soc_pct, range_at_100_mi,
    epa_mi, tesla_claimed_mi,
    soh_vs_epa_pct, soh_vs_tesla_pct,
    warranty_floor_pct, soh_headroom_pct, epa_notes,
  } = row;

  const headroomColor =
    soh_headroom_pct == null ? "#7F7F7F"
      : soh_headroom_pct <= 5  ? "#FF6B57"
      : soh_headroom_pct <= 12 ? "#FFB020"
      : "#39FF14";

  const sohEpaColor =
    soh_vs_epa_pct == null ? "#7F7F7F"
      : soh_vs_epa_pct >= 90 ? "#39FF14"
      : soh_vs_epa_pct >= 80 ? "#7FD1FF"
      : soh_vs_epa_pct >= 70 ? "#FFB020"
      : "#FF6B57";

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02] transition-colors align-top" data-testid={`fleet-row-${row.vin_last4}`}>
      <td className="px-3 py-3">
        <div className="font-medium text-white">{display_name}</div>
        <div className="text-zinc-500 text-[11px] font-mono">
          {year} {model}{trim ? ` · ${trim}` : ""}
        </div>
        {epa_notes && (
          <div className="text-zinc-600 text-[10px] italic mt-1 max-w-xs">
            {epa_notes}
          </div>
        )}
      </td>
      <td className="px-3 py-3 font-mono text-zinc-400">{vin_masked}</td>
      <td className="px-3 py-3 text-right font-mono">
        <div className="text-white">{battery_range_mi != null ? `${battery_range_mi} mi` : "—"}</div>
        <div className="text-zinc-500 text-[10px]">@ {usable_soc_pct != null ? `${usable_soc_pct}%` : "—"}</div>
      </td>
      <td className="px-3 py-3 text-right font-mono text-white">
        {range_at_100_mi != null ? `${range_at_100_mi} mi` : "—"}
      </td>
      <td className="px-3 py-3 text-right font-mono text-zinc-300">
        {epa_mi != null ? `${epa_mi} mi` : "—"}
      </td>
      <td className="px-3 py-3 text-right font-mono text-zinc-300">
        {tesla_claimed_mi != null ? `${tesla_claimed_mi} mi` : "—"}
      </td>
      <td className="px-3 py-3 text-right font-mono font-semibold" style={{ color: sohEpaColor }}>
        {soh_vs_epa_pct != null ? `${soh_vs_epa_pct}%` : "—"}
      </td>
      <td className="px-3 py-3 text-right font-mono text-zinc-300">
        {soh_vs_tesla_pct != null ? `${soh_vs_tesla_pct}%` : "—"}
      </td>
      <td className="px-3 py-3 text-right font-mono">
        <span style={{ color: headroomColor }}>
          {soh_headroom_pct != null ? `${soh_headroom_pct > 0 ? "+" : ""}${soh_headroom_pct} pt` : "—"}
        </span>
        <div className="text-zinc-500 text-[10px]">
          floor {warranty_floor_pct != null ? `${warranty_floor_pct}%` : "—"}
        </div>
      </td>
    </tr>
  );
};
